/**
 * QuotaExceededPaywall Component
 *
 * Part of Story 3.4: Hard Stop Paywall (100%)
 * Epic 3: Frontend UI Components
 *
 * Displays a non-dismissible full-page modal when user reaches 100% of their monthly quota.
 * This is the final conversion opportunity before complete blocking of agent execution.
 *
 * Acceptance Criteria:
 * - AC#1: Full-page modal (non-dismissible) when usage >= 100%
 * - AC#2: Title: "🚫 You've reached your monthly limit"
 * - AC#3: Message with quota limit, reset countdown, and upgrade option
 * - AC#4: Comparison: "Solo: 50/mo → Agency: 500/mo (10x!)"
 * - AC#5: CTA: "Upgrade to Continue" (large primary button)
 * - AC#6: Grace period countdown (if active)
 * - AC#7: Red theme (#EF4444)
 * - AC#8: Blocks all agent execution attempts
 */

import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useTierData } from '@/hooks/useTierData';
import type { Tier } from '@/types/billing';

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
}

/**
 * Calculate time remaining until a target date
 */
function calculateTimeRemaining(targetDate: Date): TimeRemaining {
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0 };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes };
}

/**
 * Tier comparison data for upgrade messaging
 */
interface TierComparison {
  current: number;
  next: number;
  nextTier: string;
  multiplier: string;
}

const TIER_COMPARISONS: Record<Tier, TierComparison> = {
  solo: { current: 50, next: 500, nextTier: 'Agency Starter', multiplier: '10x' },
  agency_starter: { current: 500, next: 1000, nextTier: 'Agency Pro', multiplier: '2x' },
  agency_pro: { current: 1000, next: 5000, nextTier: 'Enterprise', multiplier: '5x' },
  enterprise: { current: 5000, next: 10000, nextTier: 'Enterprise Plus', multiplier: '2x' },
};

interface GracePeriodBannerProps {
  gracePeriodEnd: Date;
}

/**
 * Grace Period Banner Component
 * Shows remaining time in grace period with urgent styling when < 1 hour
 */
const GracePeriodBanner: React.FC<GracePeriodBannerProps> = ({ gracePeriodEnd }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeRemaining(gracePeriodEnd));

  useEffect(() => {
    // Update every minute
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeRemaining(gracePeriodEnd));
    }, 60000);

    return () => clearInterval(interval);
  }, [gracePeriodEnd]);

  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 1;

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 ${
        isUrgent
          ? 'bg-orange-50 border-orange-400 text-orange-900'
          : 'bg-blue-50 border-blue-300 text-blue-900'
      }`}
    >
      <p className="text-sm font-semibold mb-1">⏳ Grace Period Active</p>
      <p className="text-sm">
        {timeLeft.days > 0 && `${timeLeft.days} days `}
        {timeLeft.hours} hours {timeLeft.minutes} minutes remaining
      </p>
      {isUrgent && (
        <p className="text-xs mt-1 font-semibold">⚠️ Almost expired!</p>
      )}
    </div>
  );
};

interface TierComparisonDisplayProps {
  currentTier: Tier;
}

/**
 * Tier Comparison Display Component
 * Shows current tier quota vs next tier with multiplier
 */
const TierComparisonDisplay: React.FC<TierComparisonDisplayProps> = ({ currentTier }) => {
  const comparison = TIER_COMPARISONS[currentTier];

  return (
    <div className="bg-white/[0.03] rounded-lg p-4 border-2 border-white/[0.06]">
      <p className="text-sm text-white/60 mb-3 text-center font-semibold">Compare Plans</p>
      <div className="flex items-center justify-center gap-3">
        <div className="text-center">
          <p className="text-xs text-white/50 uppercase mb-1">Current</p>
          <p className="text-lg font-bold text-white">{comparison.current}/mo</p>
        </div>
        <div className="text-2xl text-gray-400">→</div>
        <div className="text-center">
          <p className="text-xs text-green-600 uppercase font-semibold mb-1">
            {comparison.nextTier}
          </p>
          <p className="text-lg font-bold text-green-600">{comparison.next}/mo</p>
        </div>
        <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
          <span className="text-sm font-bold">{comparison.multiplier}!</span>
        </div>
      </div>
    </div>
  );
};

/**
 * QuotaExceededPaywall Component
 *
 * Non-dismissible blocking modal shown at 100% quota usage.
 * Final conversion opportunity with clear upgrade path.
 *
 * @example
 * ```tsx
 * // In AppLayout or root component
 * <QuotaExceededPaywall />
 * ```
 */
export const QuotaExceededPaywall: React.FC = () => {
  const navigate = useNavigate();
  const { data: tierData, isLoading } = useTierData();
  const [isOpen, setIsOpen] = useState(false);
  const [timeUntilReset, setTimeUntilReset] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
  });

  /**
   * Check if paywall should be shown when tier data changes
   */
  useEffect(() => {
    if (isLoading || !tierData) return;

    const shouldShow = tierData.usage_percentage >= 100;
    setIsOpen(shouldShow);

    // Calculate initial time until reset
    if (shouldShow && tierData.reset_date) {
      const resetDate = new Date(tierData.reset_date);
      setTimeUntilReset(calculateTimeRemaining(resetDate));
    }
  }, [tierData, isLoading]);

  /**
   * Update countdown timer every minute
   */
  useEffect(() => {
    if (!isOpen || !tierData?.reset_date) return;

    const resetDate = new Date(tierData.reset_date);
    const interval = setInterval(() => {
      setTimeUntilReset(calculateTimeRemaining(resetDate));
    }, 60000); // Update every 60 seconds

    return () => clearInterval(interval);
  }, [isOpen, tierData?.reset_date]);

  /**
   * Handle upgrade button click
   */
  const handleUpgrade = () => {
    // Navigate to pricing page with tier pre-selected
    const nextTier = TIER_COMPARISONS[tierData?.tier || 'solo'].nextTier;
    navigate(`/pricing?tier=${nextTier.toLowerCase().replace(' ', '-')}`);
  };

  // Don't render if no tier data
  if (!tierData) return null;

  const { current_usage, quota_limit, tier, grace_period } = tierData;
  const hasGracePeriod = grace_period?.active && grace_period?.ends_at;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {}} // Empty function = non-dismissible
        static // Prevents closing on backdrop click
      >
        {/* Backdrop - darker for critical state */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 " />
        </Transition.Child>

        {/* Full-screen modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            {/* Modal panel */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-[#0C1319] shadow-2xl transition-all"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="quota-exceeded-title"
                aria-describedby="quota-exceeded-description"
              >
                {/* NO close button - non-dismissible */}

                {/* Modal content */}
                <div className="p-6 sm:p-8">
                  {/* Icon and Title - Red Theme */}
                  <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                      <ShieldAlert className="w-8 h-8 text-red-600" />
                    </div>
                    <Dialog.Title
                      as="h2"
                      className="text-2xl font-bold text-white"
                      id="quota-exceeded-title"
                    >
                      🚫 You've reached your monthly limit
                    </Dialog.Title>
                  </div>

                  {/* Message */}
                  <Dialog.Description
                    className="text-base text-white/80 mb-6 text-center"
                    id="quota-exceeded-description"
                  >
                    You've used all{' '}
                    <span className="font-bold text-red-700">{quota_limit}</span> executions
                    this billing period.{' '}
                    {timeUntilReset.days > 0 || timeUntilReset.hours > 0 ? (
                      <>
                        Resets in{' '}
                        <span className="font-bold text-red-700">
                          {timeUntilReset.days > 0 && `${timeUntilReset.days} days, `}
                          {timeUntilReset.hours} hours
                        </span>
                      </>
                    ) : (
                      <span className="font-bold text-red-700">Resets soon</span>
                    )}{' '}
                    <span className="font-semibold">OR</span> Upgrade now to continue.
                  </Dialog.Description>

                  {/* Grace Period Banner (if active) */}
                  {hasGracePeriod && grace_period.ends_at && (
                    <div className="mb-6">
                      <GracePeriodBanner gracePeriodEnd={new Date(grace_period.ends_at)} />
                    </div>
                  )}

                  {/* Tier Comparison */}
                  <div className="mb-6">
                    <TierComparisonDisplay currentTier={tier} />
                  </div>

                  {/* Upgrade Benefits */}
                  <div className="bg-red-50 rounded-lg p-4 mb-6 border-2 border-red-200">
                    <p className="text-sm font-semibold text-red-900 mb-2">
                      ✨ {TIER_COMPARISONS[tier].nextTier} includes:
                    </p>
                    <ul className="text-sm text-red-800 space-y-1">
                      <li>
                        ✓ {TIER_COMPARISONS[tier].next} executions/month (
                        {TIER_COMPARISONS[tier].multiplier} more!)
                      </li>
                      <li>✓ More AI agents unlocked</li>
                      <li>✓ Priority support</li>
                      <li>✓ Advanced features</li>
                    </ul>
                  </div>

                  {/* Primary CTA - Large and prominent */}
                  <button
                    onClick={handleUpgrade}
                    className="
                      w-full px-8 py-4 text-lg font-bold
                      text-white bg-red-600
                      rounded-lg shadow-lg shadow-red-500/50
                      hover:bg-red-700 hover:shadow-xl hover:shadow-red-500/60
                      focus:outline-none focus:ring-4 focus:ring-red-500/50
                      transition-all duration-200
                      transform hover:scale-[1.02]
                    "
                    type="button"
                    autoFocus
                  >
                    Upgrade to Continue →
                  </button>

                  {/* Footer info */}
                  <p className="text-xs text-white/50 text-center mt-4">
                    You cannot execute agents until you upgrade or your quota resets
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

QuotaExceededPaywall.displayName = 'QuotaExceededPaywall';
